import { Box, Center, HStack } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';

import Text from 'components/Text';
import { ERoute } from 'types/frontend';

import { Mission } from './useOnboardingMissions';

interface MissionItemProps {
  mission: Mission;
  isLegacy: boolean;
  isNextMission?: boolean;
  onAction?: (missionId: string) => void;
}

const MissionItem = ({ mission, isLegacy, isNextMission, onAction }: MissionItemProps) => {
  const navigate = useNavigate();

  const showBigBadge = mission.isPremium && isLegacy && !mission.completed;
  const targetRoute = showBigBadge ? ERoute.Plans : mission.route;
  const isClickable = !mission.completed;

  const handleClick = () => {
    if (mission.completed) return;

    // Legacy upsell: BIG badge overrides custom actions for premium missions
    if (showBigBadge) {
      navigate(ERoute.Plans);
      return;
    }

    if (onAction) {
      onAction(mission.id);
      return;
    }

    if (targetRoute) {
      navigate(targetRoute);
    }
  };

  return (
    <HStack
      spacing={3}
      py={1.5}
      px={2}
      borderRadius={6}
      cursor={isClickable ? 'pointer' : 'default'}
      onClick={handleClick}
      _hover={isClickable ? { bg: 'whiteAlpha.100' } : {}}
      transition="all 0.15s ease-out"
      width="100%"
      sx={
        isNextMission
          ? {
              '@keyframes missionHighlight': {
                '0%': { backgroundColor: 'transparent' },
                '40%': { backgroundColor: 'rgba(255, 255, 255, 0.06)' },
                '100%': { backgroundColor: 'transparent' },
              },
              animation: 'missionHighlight 1.8s ease-in-out 0.3s 1',
            }
          : {}
      }
    >
      {mission.completed ? (
        <Center
          minW="20px"
          minH="20px"
          borderRadius="50%"
          bg="green.400"
        >
          <Box as="span" color="gray.900" fontSize="11px" fontWeight="bold" lineHeight="1">
            ✓
          </Box>
        </Center>
      ) : (
        <Center
          minW="20px"
          minH="20px"
          borderRadius="50%"
          border="2px solid"
          borderColor="whiteAlpha.400"
        />
      )}
      <Text
        fontSize="small"
        fontWeight="regular"
        opacity={mission.completed ? 'secondary' : 'primary'}
        textDecoration={mission.completed ? 'line-through' : 'none'}
        flex={1}
      >
        {mission.label}
      </Text>
      {showBigBadge && (
        <Center
          minW="37px"
          minH="22px"
          borderRadius={6}
          bg="linear-gradient(180deg, #F6AD55 0%, #ED8936 100%)"
          flexShrink={0}
        >
          <Text fontWeight="semibold" fontSize="xMicro">
            BIG
          </Text>
        </Center>
      )}
    </HStack>
  );
};

export default MissionItem;
